import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Download, LayoutGrid, Lock, Table2 } from "lucide-react";
import api from "@/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/contexts/AuthContext";
import { useSeo } from "@/seo";
import { CollectionSets, CollectionPlayers, CollectionMissing, CollectionWeek } from "@/components/CollectionPanels";
import { CollectionTable } from "@/components/CollectionTable";

const mono = "font-mono2 tracking-[.04em]";
const VIEW_KEY = "rr.collection.view";

function Stat({ value, label, testid }) {
  return (
    <div className="border-l-2 border-[var(--d-accent)] pl-3" data-testid={testid}>
      <div className={`${mono} text-2xl font-bold tabular-nums text-[var(--d-text)]`}>{value}</div>
      <div className={`${mono} text-[9px] uppercase text-[var(--d-muted)]`}>{label}</div>
    </div>
  );
}

function Locked({ t }) {
  return (
    <div className="flex flex-col items-start gap-3 border border-dashed border-[var(--d-accent)]/50 bg-[var(--d-panel)] p-5" data-testid="collection-locked">
      <span className={`${mono} inline-flex items-center gap-2 text-[11px] uppercase text-[var(--d-text)]`}><Lock className="h-3.5 w-3.5 text-[var(--d-accent)]" />{t("coll.locked")}</span>
      <Link to="/pricing" className={`${mono} border border-[var(--d-accent)] bg-[var(--d-accent)] px-3 py-1.5 text-[10px] font-bold uppercase text-[#0B0E14] hover:bg-transparent hover:text-[var(--d-accent)]`} data-testid="collection-locked-cta-premium">{t("coll.locked_cta")}</Link>
    </div>
  );
}

function CollectionIntro({ t }) {
  const steps = [t("coll.intro_step1"), t("coll.intro_step2"), t("coll.intro_step3")];
  return (
    <div className="dash-scope mx-auto max-w-[760px] pt-10" data-testid="collection-intro">
      <h1 className="font-sg text-3xl font-bold uppercase tracking-[-0.035em] text-[var(--d-text)] sm:text-4xl">{t("coll.intro_h1")}</h1>
      <p className={`${mono} mt-3 text-[12px] leading-relaxed text-[var(--d-muted)]`} data-testid="collection-intro-lede">{t("coll.intro_lede")}</p>
      <ol className="mt-6 space-y-3">
        {steps.map((s, i) => (
          <li key={i} className="flex gap-3 border-l-2 border-[var(--d-accent)] pl-3" data-testid={`collection-intro-step-${i + 1}`}>
            <span className={`${mono} text-[11px] font-bold text-[var(--d-accent)]`}>{i + 1}</span>
            <span className={`${mono} text-[11.5px] leading-relaxed text-[var(--d-text)]`}>{s}</span>
          </li>
        ))}
      </ol>
      <div className="mt-7 flex flex-wrap items-center gap-2">
        <Link to="/auth?next=%2Fmy-collection" className={`${mono} border border-[var(--d-accent)] bg-[var(--d-accent)] px-4 py-2 text-[11px] font-bold uppercase text-[#0B0E14] hover:bg-transparent hover:text-[var(--d-accent)]`} data-testid="collection-intro-signup">
          {t("coll.intro_signup")}
        </Link>
        <Link to="/cards" className={`${mono} border border-[var(--d-line)] px-4 py-2 text-[11px] uppercase text-[var(--d-text)] hover:border-[var(--d-accent)]`} data-testid="collection-intro-browse">
          {t("coll.intro_browse")}
        </Link>
      </div>
      <p className={`${mono} mt-4 text-[10px] uppercase text-[var(--d-muted)]`} data-testid="collection-intro-free">{t("coll.intro_free")}</p>
    </div>
  );
}

export default function MyCollection() {
  const { t, lang } = useI18n();
  const { me, loaded } = useAuth();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [view, setView] = useState(() => localStorage.getItem(VIEW_KEY) || "cards");
  useSeo(t("coll.title"), "", null);

  useEffect(() => {
    if (!me) return;
    api.get("/collection/summary", { params: { lang } }).then((r) => setD(r.data)).catch(() => navigate("/auth?next=%2Fmy-collection"));
  }, [me, lang, navigate]);

  const switchView = (v) => { setView(v); localStorage.setItem(VIEW_KEY, v); };
  const nf = useMemo(() => new Intl.NumberFormat(lang === "fr" ? "fr-FR" : "en-GB"), [lang]);

  // Un visiteur déconnecté n'est plus renvoyé vers le formulaire de connexion : il obtient
  // l'explication de ce que fait le compteur, puis l'inscription. On ne demande pas de créer
  // un compte pour une fonction qu'on n'a pas encore montrée.
  if (loaded && !me) return <CollectionIntro t={t} />;
  if (!me || !d) return <p className={`pt-16 text-center ${mono} text-sm text-slate-500`} data-testid="collection-loading">{t("common.loading")}</p>;
  const s = d.stats;
  const empty = s.unique_cards === 0;

  return (
    <div className="dash-scope mx-auto max-w-[1200px] pt-8" data-testid="my-collection-page">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3 border-b border-[var(--d-line)] pb-4">
        <div>
          <h1 className="font-sg text-3xl font-bold uppercase tracking-[-0.035em] text-[var(--d-text)] sm:text-4xl">{t("coll.title")}</h1>
          <p className={`${mono} mt-1 text-[11px] uppercase text-[var(--d-muted)]`} data-testid="collection-headline">
            {nf.format(s.cards)} {t("coll.cards")} · {nf.format(s.sets_started)} {t("coll.sets")} · {nf.format(s.players)} {t("coll.players")}
          </p>
        </div>
        {d.premium && !empty && (
          <div className="flex items-center gap-1" data-testid="collection-view-switch">
            <button type="button" onClick={() => switchView("cards")} className={`${mono} inline-flex items-center gap-1 border px-2.5 py-1.5 text-[10px] uppercase ${view === "cards" ? "border-[var(--d-accent)] text-[var(--d-accent)]" : "border-[var(--d-line)] text-[var(--d-muted)]"}`} data-testid="collection-view-cards"><LayoutGrid className="h-3 w-3" />{t("coll.view_cards")}</button>
            <button type="button" onClick={() => switchView("table")} className={`${mono} inline-flex items-center gap-1 border px-2.5 py-1.5 text-[10px] uppercase ${view === "table" ? "border-[var(--d-accent)] text-[var(--d-accent)]" : "border-[var(--d-line)] text-[var(--d-muted)]"}`} data-testid="collection-view-table"><Table2 className="h-3 w-3" />{t("coll.view_table")}</button>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/collection/export.csv`} className={`${mono} ml-2 inline-flex items-center gap-1 border border-[var(--d-line)] px-2.5 py-1.5 text-[10px] uppercase text-[var(--d-text)] hover:border-[var(--d-accent)]`} data-testid="collection-export-csv"><Download className="h-3 w-3" />{t("coll.export_csv")}</a>
          </div>
        )}
      </div>

      <div className="mb-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6" data-testid="collection-stats">
        <Stat value={nf.format(s.cards)} label={t("coll.cards")} testid="stat-cards" />
        <Stat value={nf.format(s.rookie_cards)} label={t("coll.rookie_cards")} testid="stat-rookie-cards" />
        <Stat value={nf.format(s.sets_started)} label={t("coll.sets_started")} testid="stat-sets-started" />
        <Stat value={nf.format(s.sets_completed)} label={t("coll.sets_completed")} testid="stat-sets-completed" />
        <Stat value={nf.format(s.players)} label={t("coll.distinct_players")} testid="stat-players" />
      </div>

      {empty && (
        <div className="border border-[var(--d-line)] bg-[var(--d-panel)] p-6" data-testid="collection-empty">
          <p className={`${mono} text-[11px] text-[var(--d-muted)]`}>{t("coll.empty")}</p>
          <Link to="/cards" className={`${mono} mt-3 inline-block border border-[var(--d-accent)] px-3 py-1.5 text-[10px] font-bold uppercase text-[var(--d-accent)]`} data-testid="collection-empty-cta">{t("coll.empty_cta")} →</Link>
        </div>
      )}

      {!empty && d.premium && <CollectionWeek rows={d.week} t={t} lang={lang} />}

      {!empty && view === "table" && d.premium ? (
        <CollectionTable rows={d.cards} t={t} lang={lang} />
      ) : !empty && (
        <div className="space-y-8">
          <CollectionSets sets={d.sets} t={t} />
          {d.premium ? (
            <>
              <CollectionPlayers rows={d.players} t={t} />
              <CollectionMissing rows={d.missing} markets={d.marketplaces || []} t={t} />
            </>
          ) : <Locked t={t} />}
        </div>
      )}
      <div className="h-8" />
    </div>
  );
}
